import React, { useState } from 'react';
import axios from 'axios';
import { Check, Upload, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VolunteerPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    city_state: '',
    areas_of_interest: [],
    availability: '',
    skills: '',
    id_document_url: '',
  });

  const [errors, setErrors] = useState({});

  const interestAreas = [
    { id: 'temple_service', label: 'Temple Service' },
    { id: 'teaching_support', label: 'Teaching Support' },
    { id: 'women_empowerment', label: 'Women Empowerment' },
    { id: 'food_donation', label: 'Food Donation Drives' },
    { id: 'fundraising', label: 'Fundraising' },
    { id: 'social_media', label: 'Social Media Support' },
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone)) newErrors.phone = 'Invalid phone number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.city_state.trim()) newErrors.city_state = 'City/State is required';
    if (formData.areas_of_interest.length === 0) newErrors.areas_of_interest = 'Select at least one area';
    if (!formData.availability.trim()) newErrors.availability = 'Availability is required';
    if (!formData.skills.trim()) newErrors.skills = 'Please mention your skills';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInterestChange = (interestId, checked) => {
    setFormData(prev => ({
      ...prev,
      areas_of_interest: checked
        ? [...prev.areas_of_interest, interestId]
        : prev.areas_of_interest.filter(id => id !== interestId)
    }));
    if (errors.areas_of_interest) {
      setErrors(prev => ({ ...prev, areas_of_interest: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/volunteers`, formData);
      setSubmitted(true);
      toast.success('Thank you for registering as a volunteer!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="volunteer-success">
        <Card className="max-w-lg w-full bg-white rounded-2xl border-none shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-heading text-3xl text-gray-900 mb-4">Welcome Aboard!</h2>
            <p className="font-body text-gray-600 mb-6">
              Thank you for volunteering with Dharma Astra Association. Our team will contact you soon to discuss how you can contribute to our mission.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-saffron hover:bg-saffron-dark text-white rounded-full px-8 py-3 font-body"
            >
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="volunteer-page">
      {/* Hero */}
      <section className="relative bg-terracotta py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-body text-sm uppercase tracking-wider text-saffron font-semibold">Join Our Team</span>
            <h1 className="font-heading text-4xl md:text-5xl text-white mt-4 mb-4">
              Become a Volunteer
            </h1>
            <p className="font-body text-lg text-white/80">
              Be part of our mission to preserve Dharma and serve humanity. Your time and skills can make a real difference.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white rounded-2xl border-none shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-saffron/10 rounded-full flex items-center justify-center">
                  <User className="w-7 h-7 text-saffron" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl text-gray-900">Volunteer Registration</h2>
                  <p className="font-body text-sm text-gray-500">Fill in your details to join our volunteer network</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-body text-sm text-gray-700">Full Name *</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className={`mt-1 py-3 rounded-lg font-body ${errors.full_name ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-volunteer-name"
                    />
                    {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-700">Phone Number *</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      className={`mt-1 py-3 rounded-lg font-body ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-volunteer-phone"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-700">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={`mt-1 py-3 rounded-lg font-body ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-volunteer-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <Label className="font-body text-sm text-gray-700">City / State *</Label>
                    <Input
                      name="city_state"
                      value={formData.city_state}
                      onChange={handleInputChange}
                      placeholder="e.g., Hyderabad, Telangana"
                      className={`mt-1 py-3 rounded-lg font-body ${errors.city_state ? 'border-red-500' : 'border-gray-200'}`}
                      data-testid="input-volunteer-city"
                    />
                    {errors.city_state && <p className="text-red-500 text-xs mt-1">{errors.city_state}</p>}
                  </div>
                </div>

                {/* Areas of Interest */}
                <div>
                  <Label className="font-body text-sm text-gray-700 mb-3 block">Areas of Interest *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {interestAreas.map((area) => (
                      <label
                        key={area.id}
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          formData.areas_of_interest.includes(area.id)
                            ? 'border-saffron bg-saffron/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.areas_of_interest.includes(area.id)}
                          onCheckedChange={(checked) => handleInterestChange(area.id, checked)}
                          className="data-[state=checked]:bg-saffron data-[state=checked]:border-saffron"
                          data-testid={`checkbox-${area.id}`}
                        />
                        <span className="font-body text-sm text-gray-700">{area.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.areas_of_interest && <p className="text-red-500 text-xs mt-2">{errors.areas_of_interest}</p>}
                </div>

                <div>
                  <Label className="font-body text-sm text-gray-700">Availability *</Label>
                  <Input
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    placeholder="e.g., Weekends, 4 hours per week"
                    className={`mt-1 py-3 rounded-lg font-body ${errors.availability ? 'border-red-500' : 'border-gray-200'}`}
                    data-testid="input-volunteer-availability"
                  />
                  {errors.availability && <p className="text-red-500 text-xs mt-1">{errors.availability}</p>}
                </div>

                <div>
                  <Label className="font-body text-sm text-gray-700">Skills & Experience *</Label>
                  <Textarea
                    name="skills"
                    value={formData.skills}
                    onChange={handleInputChange}
                    placeholder="Tell us about your skills, experience, and how you can contribute..."
                    rows={4}
                    className={`mt-1 rounded-lg font-body resize-none ${errors.skills ? 'border-red-500' : 'border-gray-200'}`}
                    data-testid="input-volunteer-skills"
                  />
                  {errors.skills && <p className="text-red-500 text-xs mt-1">{errors.skills}</p>}
                </div>

                <div>
                  <Label className="font-body text-sm text-gray-700">ID Document URL (Optional)</Label>
                  <Input
                    name="id_document_url"
                    value={formData.id_document_url}
                    onChange={handleInputChange}
                    placeholder="Link to ID document (Google Drive, Dropbox, etc.)"
                    className="mt-1 py-3 rounded-lg font-body border-gray-200"
                    data-testid="input-volunteer-id"
                  />
                  <p className="font-body text-xs text-gray-500 mt-1">Optional: Upload your ID to a cloud service and share the link</p>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-saffron hover:bg-saffron-dark text-white rounded-full py-6 font-body font-semibold text-lg btn-lift disabled:opacity-50"
                    data-testid="volunteer-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;
